import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Center,
  Code,
  Group,
  Loader,
  SegmentedControl,
  Stack,
  Text,
} from '@mantine/core';
import { IconAlertCircle, IconPencil, IconPlus } from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ResponsiveTable } from '../../shared/components/ResponsiveTable';
import type { DataColumn } from '../../shared/components/ResponsiveTable';
import { ApiError } from '../../shared/api/problem';
import { usePermissions } from '../auth/usePermissions';
import { listShipMethods, type ShipMethodDto } from './api';
import { ShipMethodFormModal } from './ShipMethodFormModal';

type Scope = 'active' | 'all';

/**
 * Carrier maintenance — the current product's ShipMethod screen.
 *
 * Starts empty on a new tenant and stays that way until somebody adds one: carriers live in the
 * tenant's own database, so this list is theirs rather than something the platform seeds. Until
 * one exists the carrier field on ship confirm and on a wave shipment has nothing to offer, which
 * is the gap this screen closes.
 */
export function ShipMethodsRoute() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const [modalOpen, { open, close }] = useDisclosure(false);
  const [editing, setEditing] = useState<ShipMethodDto | null>(null);
  const [scope, setScope] = useState<Scope>('active');

  const canManage = permissions.has('tenant:manage_settings');

  const query = useQuery({
    queryKey: ['ship-methods', scope] as const,
    queryFn: ({ signal }) => listShipMethods(scope === 'all', signal),
  });

  function startCreate() {
    setEditing(null);
    open();
  }

  function startEdit(method: ShipMethodDto) {
    setEditing(method);
    open();
  }

  function onSaved() {
    void queryClient.invalidateQueries({ queryKey: ['ship-methods'] });
  }

  const rows = query.data ?? [];

  /**
   * The code is the headline — it is what ship confirm offers and what people say out loud
   * ("send it UPSG"). Status becomes an aside so an inactive carrier is obvious without a label.
   */
  const columns: DataColumn<ShipMethodDto>[] = useMemo(
    () => [
      {
        key: 'code',
        label: t('shipMethods.code'),
        card: 'title',
        render: (method) => <Code>{method.shipMethodCode}</Code>,
      },
      {
        key: 'description',
        label: t('shipMethods.description'),
        card: 'subtitle',
        render: (method) => method.displayName,
      },
      {
        key: 'carrier',
        label: t('shipMethods.carrier'),
        render: (method) =>
          method.carrier === null ? (
            <Text size="sm" c="dimmed">
              —
            </Text>
          ) : (
            <Text size="sm">{method.carrier}</Text>
          ),
      },
      {
        key: 'status',
        label: t('shipMethods.status'),
        card: 'aside',
        render: (method) =>
          method.isActive ? (
            <Text size="sm" c="dimmed">
              {t('shipMethods.activeState')}
            </Text>
          ) : (
            <Badge size="sm" variant="light" color="gray">
              {t('shipMethods.inactiveState')}
            </Badge>
          ),
      },
    ],
    [t],
  );

  return (
    <>
      <PageHeader
        title={t('nav.shipMethods')}
        actions={
          canManage ? (
            <Button color="invetrak" leftSection={<IconPlus size={16} />} onClick={startCreate}>
              {t('shipMethods.new')}
            </Button>
          ) : null
        }
      />

      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {t('shipMethods.intro')}
        </Text>

        <Stack gap={4} w="fit-content">
          <Text size="sm" fw={500}>
            {t('shipMethods.show')}
          </Text>
          <SegmentedControl
            value={scope}
            onChange={(v) => setScope(v as Scope)}
            data={[
              { label: t('shipMethods.activeOnly'), value: 'active' },
              { label: t('shipMethods.all'), value: 'all' },
            ]}
          />
        </Stack>

        {query.isLoading && (
          <Center py="xl">
            <Loader color="invetrak" />
          </Center>
        )}

        {query.isError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
            title={t('shipMethods.errors.loadFailed')}
          >
            <Stack gap="xs">
              <Text size="sm">{(query.error as Error).message}</Text>
              {query.error instanceof ApiError && query.error.problem.correlationId && (
                <Text size="xs" c="dimmed">
                  {t('errors.genericBody')} <Code>{query.error.problem.correlationId}</Code>
                </Text>
              )}
              <Group>
                <Button size="xs" variant="default" onClick={() => void query.refetch()}>
                  {t('shipMethods.retry')}
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}

        {/* Empty is the normal state for a new tenant, so it says what to do rather than
            looking like something failed. */}
        {query.isSuccess && rows.length === 0 && (
          <Alert color="gray" variant="light">
            {canManage ? t('shipMethods.emptyManage') : t('shipMethods.empty')}
          </Alert>
        )}

        {query.isSuccess && rows.length > 0 && (
          <ResponsiveTable
            rows={rows}
            columns={columns}
            rowKey={(method) => method.shipMethodId}
            minWidth={560}
            actionsWidth={60}
            striped
            highlightOnHover
            // Undefined rather than an empty cell for a read-only session: the trailing column and
            // its blank header were pure furniture for anybody who cannot edit.
            actions={
              canManage
                ? (method) => (
                    <ActionIcon
                      variant="subtle"
                      color="invetrak"
                      aria-label={t('shipMethods.edit', { code: method.shipMethodCode })}
                      onClick={() => startEdit(method)}
                    >
                      <IconPencil size={16} />
                    </ActionIcon>
                  )
                : undefined
            }
          />
        )}
      </Stack>

      <ShipMethodFormModal opened={modalOpen} onClose={close} method={editing} onSaved={onSaved} />
    </>
  );
}
